import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import Divider from '@mui/material/Divider';
import { HOME_WIDGET_FONT_FAMILY } from '../homeWidgetTypography';

export type OutlookMessage = {
  id: string;
  threadId: string;
  senderName: string;
  senderInitials: string;
  senderColor: string;
  subject: string;
  preview: string;
  receivedLabel: string;
  receivedTime?: string;
  receivedDateLabel?: string;
  isUnread: boolean;
  isImportant?: boolean;
  hasAttachment?: boolean;
};

export type OutlookInboxData = {
  unreadCount: number;
  messages: OutlookMessage[];
  threads: OutlookThread[];
};

export type OutlookThreadMessage = {
  id: string;
  senderName: string;
  senderEmail?: string;
  senderInitials: string;
  senderColor: string;
  recipients: string;
  sentLabel: string;
  body: string;
  isImportant?: boolean;
  attachments?: {
    id: string;
    name: string;
    size?: string;
  }[];
};

export type OutlookThread = {
  id: string;
  subject: string;
  messages: OutlookThreadMessage[];
};

type OutlookInboxProps = {
  messages: OutlookMessage[];
  onViewAll: () => void;
  onMessageClick: (threadId: string) => void;
  onRefresh: () => void;
};

const PREVIEW_EMAIL_COUNT = 4;

const OutlookInbox = ({
  messages,
  onViewAll,
  onMessageClick,
  onRefresh,
}: OutlookInboxProps) => {
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';
  const visibleMessages = messages.slice(0, PREVIEW_EMAIL_COUNT);
  const hasMoreEmails = messages.length > PREVIEW_EMAIL_COUNT;
  const [selectedMessageId, setSelectedMessageId] = React.useState<
    string | null
  >(null);

  const handleMessageSelect = (message: OutlookMessage) => {
    setSelectedMessageId(message.id);
    onMessageClick(message.threadId);
  };

  return (
    <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
      <Box
        flex={1}
        minHeight={0}
        sx={{
          maxHeight: '440px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {visibleMessages.map((message, index) => (
          <React.Fragment key={message.id}>
            <Box
              display="flex"
              gap={1}
              alignItems="flex-start"
              width="100%"
              height="89px"
              py={1}
              px={{ xs: 1, sm: 2 }}
              mb={message.isUnread ? 1 : 0}
              sx={{
                cursor: 'pointer',
                boxSizing: 'border-box',
                bgcolor:
                  selectedMessageId === message.id
                    ? isDarkMode
                      ? '#000000'
                      : 'rgba(69, 33, 136, 0.08)'
                    : 'transparent',

                '&:hover': {
                  bgcolor: isDarkMode ? '#000000' : 'rgba(69, 33, 136, 0.04)',
                },
              }}
              role="button"
              tabIndex={0}
              onClick={() => handleMessageSelect(message)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleMessageSelect(message);
                }
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={{ xs: 0.5, sm: 0.75 }}
                flexShrink={0}
              >
                <Box
                  width={8}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {message.isUnread ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#0277BD',
                      }}
                    />
                  ) : null}
                </Box>

                <Avatar
                  variant="circular"
                  sx={{
                    bgcolor: '#5D536C',
                    width: { xs: '36px', sm: '40px' },
                    height: { xs: '36px', sm: '40px' },
                    minWidth: { xs: '36px', sm: '40px' },
                    minHeight: { xs: '36px', sm: '40px' },
                    fontSize: '16px',
                    flexShrink: 0,
                  }}
                >
                  {message.senderInitials}
                </Avatar>
              </Box>

              <Box display="flex" flexDirection="column" minWidth={0} flex={1}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      fontFamily: HOME_WIDGET_FONT_FAMILY,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '0.15px',
                      fontWeight: message.isUnread ? 500 : 400,
                      color: isDarkMode ? '#FFFFFF' : 'rgba(0, 0, 0, 0.87)',
                    }}
                  >
                    {message.senderName}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.75}
                    flexShrink={0}
                  >
                    {message.isImportant ? (
                      <PriorityHighIcon
                        sx={{ color: '#D32F2F', fontSize: 20 }}
                      />
                    ) : null}
                    {message.hasAttachment ? (
                      <AttachFileIcon
                        sx={{
                          color: isDarkMode ? 'rgb(208, 188, 250)' : '#6B6B6B',
                          fontSize: 22,
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mt={0.4}
                >
                  <Typography
                    variant="body1"
                    noWrap
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      fontFamily: HOME_WIDGET_FONT_FAMILY,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '0.14px',
                      fontWeight: message.isUnread ? 500 : 400,
                      color: message.isUnread
                        ? '#01579B'
                        : isDarkMode
                        ? '#FFFFFF'
                        : 'rgba(0, 0, 0, 0.87)',
                    }}
                  >
                    {message.subject}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{
                      fontFamily: HOME_WIDGET_FONT_FAMILY,
                      fontSize: '12px',
                      lineHeight: '16px',
                      flexShrink: 0,
                    }}
                  >
                    {message.receivedLabel === 'Today'
                      ? message.receivedTime
                      : message.receivedLabel === 'Yesterday'
                      ? `Tue ${message.receivedTime ?? ''}`
                      : message.receivedDateLabel || message.receivedLabel}
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  noWrap
                  mt={0.5}
                  sx={{
                    width: '100%',
                    fontFamily: HOME_WIDGET_FONT_FAMILY,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    fontWeight: 400,
                    color: isDarkMode ? 'text.secondary' : 'rgba(0, 0, 0, 0.6)',
                  }}
                >
                  {message.preview}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ width: '100%', mx: 0, my: 0 }} />
          </React.Fragment>
        ))}
      </Box>

      {hasMoreEmails ? (
        <Box
          display="flex"
          gap={1.5}
          mt="auto"
          sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
        >
          <Button
            kind="secondary"
            onClick={onRefresh}
            sx={{
              flex: 1,
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'transparent',
                border: 'none',
              },
            }}
            startIcon={
              <SyncOutlinedIcon
                sx={{
                  color: 'primary.main !important',
                  fontSize: 22,
                  transform: 'rotate(90deg)',
                }}
              />
            }
          >
            Sync now
          </Button>
          <Button
            kind="secondary"
            onClick={onViewAll}
            sx={{
              flex: 1,
              height: 42,
              minHeight: 42,
              py: 1,
              px: '22px',
            }}
            endIcon={<EastOutlinedIcon sx={{ fontSize: 24 }} />}
          >
            View all
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default OutlookInbox;
